import React, { useMemo } from 'react';
import Modal from '../../components/Modal';
import './CodeComparePicker.css';

/**
 * F12.4 — Pick a target snippet to diff against.
 *
 * Stripped-down modal: list every candidate snippet (already pre-filtered by
 * the caller to same-`lang`) with a brief preview, click to select. The
 * orchestrator (MessageList) feeds it the source + the candidate set.
 *
 * Props:
 *  - isOpen, onClose
 *  - source: { lang, title?, text } — used only for the heading
 *  - snippets: Array<{ id, lang, title?, text, senderName?, sentAt? }>
 *  - onPick: (snippet) => void
 */
const CodeComparePicker = ({ isOpen, onClose, source, snippets, onPick }) => {
  const list = useMemo(() => Array.isArray(snippets) ? snippets : [], [snippets]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="card"
      size="md"
      title={`Compare ${source?.title || `${source?.lang || ''} snippet`} with…`}
      ariaLabel="Compare with another snippet"
      className="compare-picker-modal"
    >
      <div className="compare-picker" data-testid="compare-picker">
        {list.length === 0 ? (
          <p className="compare-picker__empty">
            No other {source?.lang ? `${source.lang.toUpperCase()} ` : ''}snippets in this room to compare against.
          </p>
        ) : (
          <ul className="compare-picker__list" aria-label="Pick a snippet">
            {list.map((s) => {
              const firstLine = (s.text || '').split('\n')[0];
              const preview = firstLine.length > 80
                ? `${firstLine.slice(0, 77)}…`
                : firstLine;
              return (
                <li key={s.id} className="compare-picker__item">
                  <button
                    type="button"
                    className="compare-picker__button"
                    onClick={() => onPick(s)}
                  >
                    <span className="compare-picker__title">
                      {s.title || `${s.lang || 'snippet'}`}
                      {s.senderName && (
                        <span className="compare-picker__sender">
                          {' '}· {s.senderName}
                        </span>
                      )}
                    </span>
                    <span className="compare-picker__preview">{preview}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default CodeComparePicker;
